import React, { useEffect, useRef } from 'react';
import { useProductService, useRegionCurrency } from '@skin-seoul/service';
import { Text, HStack, Box } from '@skin-seoul/design-system';
import { useProductCompareContext } from '../../context';
import AIInsightSkeleton from './AiInsight.skeleton';
type AIInsightProps = {
  orgProductId: number;
  altProductId: number;
  isExpanded: boolean;
  onIsTruncatedChange?: (isTruncated: boolean) => void;
};
const AIInsight = ({
  orgProductId,
  altProductId,
  isExpanded,
  onIsTruncatedChange
}: AIInsightProps) => {
  const textRef = useRef<HTMLParagraphElement>(null);
  const {
    currency
  } = useRegionCurrency();
  const {
    useGetAIInsight,
    useGetListRecommendations
  } = useProductService();
  const {
    selectedFilter
  } = useProductCompareContext();
  const {
    data: recommendationsData,
    isLoading: isRecommendationsLoading
  } = useGetListRecommendations({
    productId: orgProductId,
    currency,
    filter: selectedFilter
  });
  const orgProduct = recommendationsData?.original;
  const altProduct = recommendationsData?.recommendations.find(rec => (rec.parentProductId ?? rec.productId) === altProductId);
  const {
    data: aiInsight,
    isLoading: isAIInsightLoading
  } = useGetAIInsight({
    orgProductId: orgProduct?.productId ?? orgProductId,
    altProductId: altProduct?.productId ?? altProductId
  });
  const isLoading = isRecommendationsLoading || isAIInsightLoading || !aiInsight;
  useEffect(() => {
    if (!textRef.current || !onIsTruncatedChange || !aiInsight) {
      return;
    }
    const checkTruncation = () => {
      if (textRef.current) {
        const isTruncated = textRef.current.scrollHeight > textRef.current.clientHeight;
        onIsTruncatedChange(isTruncated);
      }
    };
    checkTruncation();
    window.addEventListener('resize', checkTruncation);
    return () => window.removeEventListener('resize', checkTruncation);
  }, [aiInsight?.insight, onIsTruncatedChange, aiInsight]);
  if (isLoading) {
    return <AIInsightSkeleton />;
  }
  return <Box width="full" position="relative" data-sentry-element="Box" data-sentry-component="AIInsight" data-sentry-source-file="AiInsight.tsx">
      <HStack width="full" alignItems="end" gap={2} data-sentry-element="HStack" data-sentry-source-file="AiInsight.tsx">
        <Text ref={textRef} textStyle="paragraph.small" fontWeight="regular" color="neutral.500" lineClamp={isExpanded ? undefined : 3} flex={1} data-sentry-element="Text" data-sentry-source-file="AiInsight.tsx">
          {aiInsight.insight}
        </Text>
      </HStack>
    </Box>;
};
AIInsight.Skeleton = AIInsightSkeleton;
export default AIInsight;